import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  useTheme,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { MobileDatePicker, MobileTimePicker } from "@mui/x-date-pickers";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

const toIsoFromLocalDateTime = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return null;
  const parsed = new Date(`${dateValue}T${timeValue}`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const formatDateInputValue = (value) => {
  const parsed = value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, "0");
  const day = String(safeDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizePickerTimeValue = (pickerValue) => {
  if (!pickerValue?.isValid?.()) {
    return "";
  }

  return pickerValue
    .second(0)
    .millisecond(0)
    .format("HH:mm");
};

const TimesheetsManualNewEdit = ({ onTimeslotCreated }) => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { overlayName, overlayData, setOverlayName, setOverlayData, showSnackbar } = useStateContext();
  const getFieldBorderSx = useCallback(
    (hasValue, hasError = false) => ({
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: hasError
            ? theme.palette.error.main
            : hasValue
              ? theme.palette.primary.main
              : theme.palette.grey[400],
        },
        "&:hover fieldset": {
          borderColor: hasError
            ? theme.palette.error.main
            : hasValue
              ? theme.palette.primary.main
              : theme.palette.grey[500],
        },
        "&.Mui-focused fieldset": {
          borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
        },
      },
      "& .MuiInputLabel-root": {
        color: hasError
          ? theme.palette.error.main
          : hasValue
            ? theme.palette.primary.main
            : theme.palette.grey[600],
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: hasError ? theme.palette.error.main : theme.palette.primary.main,
      },
    }),
    [theme]
  );


  const [manualError, setManualError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCheckingDaySlots, setIsCheckingDaySlots] = useState(false);
  const [showFutureDateConfirm, setShowFutureDateConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [dayTimeslots, setDayTimeslots] = useState([]);
  const [activeInProgressEntry, setActiveInProgressEntry] = useState(null);
  const [manualForm, setManualForm] = useState({
    date: formatDateInputValue(new Date()),
    clockInTime: "",
    clockOutTime: "",
    notes: "",
  });
  const isEditMode = useMemo(
    () => Boolean(overlayData?.timesheet?.id),
    [overlayData?.timesheet?.id]
  );
  const editingTimesheetId = overlayData?.timesheet?.id ?? null;
  const isFutureDate = useMemo(() => {
    if (!manualForm.date) {
      return false;
    }

    const selectedDate = dayjs(manualForm.date);
    return selectedDate.isValid() && selectedDate.isAfter(dayjs(), "day");
  }, [manualForm.date]);

  const toMinutes = useCallback((timeValue) => {
    if (!timeValue || !/^\d{2}:\d{2}$/.test(timeValue)) {
      return null;
    }
    const [hours, minutes] = timeValue.split(":").map((part) => Number(part));
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
      return null;
    }
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }
    return hours * 60 + minutes;
  }, []);

  const normalizedDaySlots = useMemo(() => {
    if (!manualForm.date) {
      return [];
    }

    const dayStart = dayjs(`${manualForm.date}T00:00`);
    const dayEnd = dayStart.add(1, "day");

    return dayTimeslots
      .map((slot) => {
        if (editingTimesheetId && Number(slot.id) === Number(editingTimesheetId)) {
          return null;
        }

        const rawStart = dayjs(slot.ClockInAt);
        const rawEnd = slot.ClockOutAt ? dayjs(slot.ClockOutAt) : null;

        if (!rawStart.isValid()) {
          return null;
        }

        const clippedStart = rawStart.isBefore(dayStart) ? dayStart : rawStart;
        const clippedEnd = rawEnd
          ? (rawEnd.isAfter(dayEnd) ? dayEnd : rawEnd)
          : dayEnd;

        const startMinutes = clippedStart.diff(dayStart, "minute");
        const endMinutes = clippedEnd.diff(dayStart, "minute");

        if (endMinutes <= startMinutes) {
          return null;
        }

        return {
          start: Math.max(0, startMinutes),
          end: Math.min(24 * 60, endMinutes),
        };
      })
      .filter(Boolean);
  }, [dayTimeslots, editingTimesheetId, manualForm.date]);

  const hasOtherActiveInProgress = useMemo(() => {
    if (!activeInProgressEntry) {
      return false;
    }

    if (isEditMode && editingTimesheetId && Number(activeInProgressEntry.id) === Number(editingTimesheetId)) {
      return false;
    }

    return true;
  }, [activeInProgressEntry, editingTimesheetId, isEditMode]);

  const canSaveWithoutClockOut = useMemo(() => {
    if (!manualForm.date || !manualForm.clockInTime || hasOtherActiveInProgress) {
      return false;
    }

    const selectedDate = dayjs(manualForm.date);
    if (!selectedDate.isValid() || !selectedDate.isSame(dayjs(), "day")) {
      return false;
    }

    const clockInAt = dayjs(`${manualForm.date}T${manualForm.clockInTime}`);
    if (!clockInAt.isValid()) {
      return false;
    }

    return clockInAt.isBefore(dayjs());
  }, [hasOtherActiveInProgress, manualForm.date, manualForm.clockInTime]);

  const overlapError = useMemo(() => {
    const newStart = toMinutes(manualForm.clockInTime);
    const newEnd = toMinutes(manualForm.clockOutTime);

    if (newStart === null) {
      return "";
    }

    if (newEnd === null) {
      if (!canSaveWithoutClockOut) {
        return "";
      }

      for (const slot of normalizedDaySlots) {
        const overlapsExisting = newStart < slot.end;
        if (overlapsExisting) {
          return "This in-progress timeslot overlaps an existing or in-progress timeslot on this day.";
        }
      }

      return "";
    }

    if (newEnd <= newStart) {
      // Overnight slot: clock-out before clock-in means next day — allowed
      // only when the resulting shift is sane (≤ 12h). 3 PM → 1 PM = 22h is a mistake.
      const overnightEnd = newEnd + 24 * 60;
      if (overnightEnd - newStart > 12 * 60) {
        return "Clock out time must be later than clock in time (overnight shifts are only allowed up to 12 hours).";
      }
      for (const slot of normalizedDaySlots) {
        const startInsideExisting = newStart >= slot.start && newStart < slot.end;
        const endInsideExisting = overnightEnd > slot.start && overnightEnd <= slot.end;
        const overlapsExisting = newStart < slot.end && slot.start < overnightEnd;
        if (startInsideExisting || endInsideExisting || overlapsExisting) {
          return "This timeslot overlaps an existing or in-progress timeslot on this day.";
        }
      }
      return "";
    }

    for (const slot of normalizedDaySlots) {
      const startInsideExisting = newStart >= slot.start && newStart < slot.end;
      const endInsideExisting = newEnd > slot.start && newEnd <= slot.end;
      const overlapsExisting = newStart < slot.end && slot.start < newEnd;

      if (startInsideExisting || endInsideExisting || overlapsExisting) {
        return "This timeslot overlaps an existing or in-progress timeslot on this day.";
      }
    }

    return "";
  }, [canSaveWithoutClockOut, manualForm.clockInTime, manualForm.clockOutTime, normalizedDaySlots, toMinutes]);

  const startOverlapError = useMemo(() => {
    const newStart = toMinutes(manualForm.clockInTime);
    if (newStart === null) {
      return "";
    }

    for (const slot of normalizedDaySlots) {
      const startInsideExisting = newStart >= slot.start && newStart < slot.end;
      if (startInsideExisting) {
        return "Clock in time is inside an existing or in-progress timeslot on this day.";
      }
    }

    return "";
  }, [manualForm.clockInTime, normalizedDaySlots, toMinutes]);

  const endOverlapError = useMemo(() => {
    const newEnd = toMinutes(manualForm.clockOutTime);
    const newStart = toMinutes(manualForm.clockInTime);
    if (newEnd === null) {
      return "";
    }

    // Overnight slot: the end time belongs to the NEXT day, so comparing it
    // against same-day slots would false-positive (e.g. 12:45 AM end vs a
    // 00:00–08:00 slot). The server checks real datetime overlap.
    if (newStart !== null && newEnd <= newStart) {
      return "";
    }

    for (const slot of normalizedDaySlots) {
      const endInsideExisting = newEnd > slot.start && newEnd <= slot.end;
      if (endInsideExisting) {
        return "Clock out time is inside an existing or in-progress timeslot on this day.";
      }
    }

    return "";
  }, [manualForm.clockInTime, manualForm.clockOutTime, normalizedDaySlots, toMinutes]);



  const liveOverlapError = startOverlapError || endOverlapError || overlapError;

  const isClockOutRequired = !canSaveWithoutClockOut;

  const isSaveDisabled =
    isSubmitting ||
    isDeleting ||
    isCheckingDaySlots ||
    !manualForm.date ||
    !manualForm.clockInTime ||
    (isClockOutRequired && !manualForm.clockOutTime) ||
    !!liveOverlapError;

  useEffect(() => {
    let cancelled = false;

    const fetchDayTimeslots = async () => {
      if (!manualForm.date) {
        setDayTimeslots([]);
        return;
      }

      setIsCheckingDaySlots(true);
      try {
        const response = await api.get("/timesheets/me/day", {
          params: { date: manualForm.date },
        });
        if (!cancelled) {
          setDayTimeslots(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (!cancelled) {
          setDayTimeslots([]);
          const apiMessage = error?.response?.data?.error;
          setManualError(apiMessage || "Failed to load timeslots for selected date.");
        }
      } finally {
        if (!cancelled) {
          setIsCheckingDaySlots(false);
        }
      }
    };

    fetchDayTimeslots();

    return () => {
      cancelled = true;
    };
  }, [manualForm.date]);

  useEffect(() => {
    let cancelled = false;

    const fetchActiveStatus = async () => {
      try {
        const response = await api.get("/timesheets/me/status");
        if (!cancelled) {
          setActiveInProgressEntry(response?.data?.activeEntry || null);
        }
      } catch (error) {
        if (!cancelled) {
          setActiveInProgressEntry(null);
        }
      }
    };

    if (overlayName === OVERLAY_NAMES.SETTINGS_TIMESHEETS_MANUAL_NEW) {
      fetchActiveStatus();
    }

    return () => {
      cancelled = true;
    };
  }, [overlayName]);

  useEffect(() => {
    if (overlayName !== OVERLAY_NAMES.SETTINGS_TIMESHEETS_MANUAL_NEW) {
      return;
    }

    const timesheet = overlayData?.timesheet;
    if (!timesheet) {
      setManualForm({
        date: formatDateInputValue(new Date()),
        clockInTime: "",
        clockOutTime: "",
        notes: "",
      });
      setManualError("");
      setShowFutureDateConfirm(false);
      setShowDeleteConfirm(false);
      return;
    }

    const clockInDate = timesheet.ClockInAt ? new Date(timesheet.ClockInAt) : null;
    const clockOutDate = timesheet.ClockOutAt ? new Date(timesheet.ClockOutAt) : null;

    setManualForm({
      date: formatDateInputValue(clockInDate || new Date()),
      clockInTime:
        clockInDate && !Number.isNaN(clockInDate.getTime())
          ? `${String(clockInDate.getHours()).padStart(2, "0")}:${String(clockInDate.getMinutes()).padStart(2, "0")}`
          : "",
      clockOutTime:
        clockOutDate && !Number.isNaN(clockOutDate.getTime())
          ? `${String(clockOutDate.getHours()).padStart(2, "0")}:${String(clockOutDate.getMinutes()).padStart(2, "0")}`
          : "",
      notes: timesheet.Notes || "",
    });
    setManualError("");
    setShowFutureDateConfirm(false);
    setShowDeleteConfirm(false);
  }, [overlayData?.timesheet, overlayName]);

  const closeOverlay = useCallback(() => {
    setOverlayName("");
    setOverlayData(undefined);
    setManualError("");
    setShowFutureDateConfirm(false);
    setShowDeleteConfirm(false);
    setManualForm({
      date: formatDateInputValue(new Date()),
      clockInTime: "",
      clockOutTime: "",
      notes: "",
    });
  }, [setOverlayData, setOverlayName]);

  const handleManualInputChange = useCallback((field, value) => {
    setManualError("");
    setManualForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleCreateManualTimeslot = useCallback(async (skipFutureConfirmation = false) => {
    setManualError("");

    if (!manualForm.date || !manualForm.clockInTime) {
      setManualError("Date and clock in time are required.");
      return;
    }

    if (!manualForm.clockOutTime && !canSaveWithoutClockOut) {
      setManualError("Clock out time is required unless this is a same-day in-progress timeslot.");
      return;
    }

    if (isFutureDate && !skipFutureConfirmation) {
      setShowFutureDateConfirm(true);
      return;
    }

    if (liveOverlapError) {
      setManualError(liveOverlapError);
      return;
    }

    const clockInAtIso = toIsoFromLocalDateTime(manualForm.date, manualForm.clockInTime);
    if (!clockInAtIso) {
      setManualError("Date and clock in time are invalid.");
      return;
    }

    let clockOutAtIso = manualForm.clockOutTime
      ? toIsoFromLocalDateTime(manualForm.date, manualForm.clockOutTime)
      : null;

    if (manualForm.clockOutTime && !clockOutAtIso) {
      setManualError("Clock out time is invalid.");
      return;
    }

    if (clockOutAtIso && new Date(clockOutAtIso) < new Date(clockInAtIso)) {
      // Overnight slot: clock-out before clock-in means next day — allowed
      // only when the resulting shift is sane (≤ 12h). 3 PM → 1 PM = 22h is a mistake.
      const overnightDurationMs =
        new Date(clockOutAtIso).getTime() +
        24 * 60 * 60 * 1000 -
        new Date(clockInAtIso).getTime();
      if (overnightDurationMs > 12 * 60 * 60 * 1000) {
        setManualError("Clock out time cannot be earlier than clock in time (overnight shifts are only allowed up to 12 hours).");
        return;
      }
      clockOutAtIso = new Date(
        new Date(clockOutAtIso).getTime() + 24 * 60 * 60 * 1000,
      ).toISOString();
    }

    setIsSubmitting(true);
    setShowFutureDateConfirm(false);
    try {
      const requestedHourlyRate = Number(currentUser?.HourlyRate ?? currentUser?.hourlyRate ?? 0);
      const payload = {
        clockInAt: clockInAtIso,
        clockOutAt: clockOutAtIso,
        notes: manualForm.notes?.trim() || null,
        ...(Number.isFinite(requestedHourlyRate) && requestedHourlyRate > 0
          ? { hourlyRateSnapshot: requestedHourlyRate }
          : {}),
      };

      if (isEditMode && editingTimesheetId) {
        await api.put(`/timesheets/manual/${editingTimesheetId}`, payload);
      } else {
        await api.post("/timesheets/manual", payload);
      }

      showSnackbar(
        isEditMode ? "Manual timeslot updated successfully" : "Manual timeslot created successfully",
        theme,
        "success"
      );
      if (onTimeslotCreated) {
        onTimeslotCreated();
      }
      closeOverlay();
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      setManualError(apiMessage || "Failed to create manual timeslot.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    manualForm,
    isEditMode,
    editingTimesheetId,
    isFutureDate,
    liveOverlapError,
    canSaveWithoutClockOut,
    closeOverlay,
    currentUser?.HourlyRate,
    currentUser?.hourlyRate,
    onTimeslotCreated,
    showSnackbar,
    theme,
  ]);

  const handleDeleteTimeslot = useCallback(async () => {
    if (!isEditMode || !editingTimesheetId) {
      return;
    }

    setManualError("");
    setShowDeleteConfirm(false);
    setIsDeleting(true);

    try {
      await api.delete(`/timesheets/manual/${editingTimesheetId}`);

      showSnackbar("Manual timeslot deleted successfully", theme, "success");
      if (onTimeslotCreated) {
        onTimeslotCreated();
      }
      closeOverlay();
    } catch (error) {
      const apiMessage = error?.response?.data?.error;
      setManualError(apiMessage || "Failed to delete manual timeslot.");
    } finally {
      setIsDeleting(false);
    }
  }, [
    closeOverlay,
    editingTimesheetId,
    isEditMode,
    onTimeslotCreated,
    showSnackbar,
    theme,
  ]);

  const footerActions = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        width: "100%",
      }}
    >
      <Button
        variant="outlined"
        onClick={() => {
          setOverlayData(undefined);
          setOverlayName(OVERLAY_NAMES.SETTINGS_TIMESHEETS_IMPORT);
        }}
        disabled={isSubmitting || isDeleting}
        sx={{
          borderRadius: "8px",
          textTransform: "none",
          px: 3,
          color: theme.palette.info.main,
          borderColor: theme.palette.info.main,
          "&:hover": {
            borderColor: theme.palette.info.main,
            backgroundColor: theme.palette.info.light,
          },
        }}
      >
        Batch Import
      </Button>

      <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
        <Button
          onClick={closeOverlay}
          disabled={isSubmitting || isDeleting}
          variant="outlined"
          sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
        >
          Cancel
        </Button>
        {isEditMode && (
          <Button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isSubmitting || isDeleting}
            variant="outlined"
            color="error"
            sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleCreateManualTimeslot}
          disabled={isSaveDisabled}
          sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
        >
          {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Save"}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <Overlay
        id="timesheets-manual-new"
        open={true}
        onClose={closeOverlay}
        title={isEditMode ? "Edit Manual Timeslot" : "Add Manual Timeslot"}
        height="fit-content"
        maxWidthPx={860}
        backButtonProps={{ disabled: isSubmitting || isDeleting }}
        footer={footerActions}
      >
        <Box
          component="form"
          sx={{
            display: "flex",
            p: "1rem 2.3rem 1rem 2.5rem",
            width: "100%",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
          onSubmit={(e) => {
            e.preventDefault();
            handleCreateManualTimeslot();
          }}
        >
            <Grid container spacing={2} width="100%">
              {manualError && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error">{manualError}</Alert>
                </Grid>
              )}

              {!manualError && liveOverlapError && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error">{liveOverlapError}</Alert>
                </Grid>
              )}

              {!manualError && !liveOverlapError && hasOtherActiveInProgress && !manualForm.clockOutTime && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">
                    You already have an in-progress timeslot. Add a clock out time, or close the active timeslot first.
                  </Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12, md: 4 }}>
                <MobileDatePicker
                  label="Date"
                  format="MM/DD/YYYY"
                  value={manualForm.date ? dayjs(manualForm.date) : null}
                  onChange={(newValue) => {
                    const formatted = newValue?.isValid?.()
                      ? newValue.format("YYYY-MM-DD")
                      : "";
                    handleManualInputChange("date", formatted);
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: getFieldBorderSx(!!manualForm.date),
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <MobileTimePicker
                  label="Clock In Time"
                  ampm
                  value={
                    manualForm.clockInTime
                      ? dayjs(`2000-01-01T${manualForm.clockInTime}`)
                      : null
                  }
                  onChange={(newValue) => {
                    const formatted = normalizePickerTimeValue(newValue);
                    handleManualInputChange("clockInTime", formatted);
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: !!startOverlapError,
                      helperText: startOverlapError || "",
                      sx: getFieldBorderSx(!!manualForm.clockInTime, !!startOverlapError),
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <MobileTimePicker
                  label="Clock Out Time"
                  ampm
                  value={
                    manualForm.clockOutTime
                      ? dayjs(`2000-01-01T${manualForm.clockOutTime}`)
                      : null
                  }
                  onChange={(newValue) => {
                    const formatted = normalizePickerTimeValue(newValue);
                    handleManualInputChange("clockOutTime", formatted);
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: !!endOverlapError,
                      helperText: endOverlapError || (canSaveWithoutClockOut ? "Optional for in-progress when start time is earlier than now." : hasOtherActiveInProgress ? "Clock out is required while another timeslot is in progress." : ""),
                      sx: getFieldBorderSx(!!manualForm.clockOutTime, !!endOverlapError),
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Notes (Optional)"
                  value={manualForm.notes}
                  onChange={(event) => handleManualInputChange("notes", event.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                  sx={getFieldBorderSx(!!manualForm.notes?.trim())}
                />
              </Grid>
              </Grid>
        </Box>
      </Overlay>

      <Dialog
        open={showFutureDateConfirm}
        onClose={() => setShowFutureDateConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Future Date Selected
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.primary }}>
            The selected date is in the future. Do you want to add this timeslot anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setShowFutureDateConfirm(false)}
            disabled={isSubmitting || isDeleting}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleCreateManualTimeslot(true)}
            disabled={isSubmitting || isDeleting}
            variant="contained"
            sx={{ borderRadius: "8px" }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Delete Timeslot?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.primary }}>
            This timeslot will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            disabled={isDeleting || isSubmitting}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTimeslot}
            disabled={isDeleting || isSubmitting}
            color="error"
            variant="contained"
            sx={{ borderRadius: "8px" }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TimesheetsManualNewEdit;
